import { Link, Navigate, useParams } from "react-router-dom";
import { Download, ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n";
import { PageHero } from "@/pages/PageHero";
import { DOCS } from "@/components/home/DocsTeaser";
import { Reveal } from "@/components/motion";
import { img } from "@/lib/gallery";

const IMAGES = { commercial: [1, 4, 16], technical: [3, 9, 7, 8], manual: [17, 18, 12, 11] };
const slugId = (h) => h.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Section = ({ s, i }) => (
  <Reveal className="scroll-mt-28 border-t border-white/12 py-8" id={slugId(s.h)}>
    <h2 className="font-display text-3xl font-extrabold uppercase">{s.h}</h2>
    {s.p && <p className="mt-3 max-w-2xl leading-relaxed text-[var(--steel)]">{s.p}</p>}
    {s.list && (
      <ul className="mt-4 space-y-2">
        {s.list.map((x) => <li key={x} className="flex gap-3 text-[var(--steel)]"><span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-[var(--gold)]" />{x}</li>)}
      </ul>
    )}
  </Reveal>
);

const SpecTable = ({ doc }) => (
  <div className="mt-4 border-t border-white/12 py-8" data-testid="spec-table">
    <h2 className="font-display text-3xl font-extrabold uppercase">{doc.specsTitle}</h2>
    <dl className="mt-5 divide-y divide-white/10 border-y border-white/10">
      {doc.specs.map(([k, v], index) => (
        <div key={k} className="grid gap-1 py-3 sm:grid-cols-5" data-testid={`technical-spec-row-${index + 1}`}>
          <dt className="font-mono text-xs uppercase tracking-[0.12em] text-[var(--steel-dim)] sm:col-span-2">{k}</dt>
          <dd className="text-sm sm:col-span-3" data-testid={`technical-spec-value-${index + 1}`}>{v}</dd>
        </div>
      ))}
    </dl>
  </div>
);

export default function DocPage() {
  const { slug } = useParams();
  const { t } = useI18n();
  const meta = DOCS.find((d) => d.slug === slug);
  if (!meta) return <Navigate to="/documentation" replace />;
  const doc = t.docs[meta.key];
  const imgs = IMAGES[meta.key];
  return (
    <div data-testid={`doc-page-${meta.key}`}>
      <PageHero eyebrow={`MB300 · by NELVIS FRANCE · ${t.docs.eyebrow}`} title={doc.title} text={doc.short} image={img(meta.n)} />
      <section className="container-x grid gap-12 pb-28 lg:grid-cols-12">
        <aside className="lg:col-span-3">
          <div className="space-y-8 lg:sticky lg:top-24">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">{t.docs.toc}</p>
              <ul className="mt-4 space-y-2 text-sm">
                {meta.key === "technical" && <li><a href="#spec-table" className="text-[var(--steel)] hover:text-white">{doc.specsTitle}</a></li>}
                {doc.sections.map((s) => <li key={s.h}><a href={`#${slugId(s.h)}`} className="text-[var(--steel)] hover:text-white">{s.h}</a></li>)}
              </ul>
            </div>
            <button disabled data-testid="doc-download-btn" className="flex w-full cursor-not-allowed items-center justify-between gap-3 border border-white/20 px-5 py-4 text-left text-xs uppercase tracking-[0.14em] text-[var(--steel)]">
              <span>{t.common.download}<span className="mt-1 block font-mono text-[10px] text-[var(--gold)]">{t.common.soon}</span></span>
              <Download className="h-4 w-4" />
            </button>
            <Link to="/commander" data-testid="doc-order-btn" className="flex items-center justify-between bg-[var(--gold)] px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-[var(--night)]">{t.common.order}<ArrowUpRight className="h-4 w-4" /></Link>
          </div>
        </aside>
        <div className="lg:col-span-9">
          <div className="mb-8 grid grid-cols-2 gap-2 md:grid-cols-4">
            {imgs.map((n) => <div key={n} className="aspect-[4/3] overflow-hidden"><img src={img(n)} alt={t.gallery.items[n - 1].t} className="h-full w-full object-cover" /></div>)}
          </div>
          {meta.key === "technical" && <div id="spec-table" className="scroll-mt-28"><SpecTable doc={doc} /></div>}
          {doc.sections.map((s, i) => <Section key={s.h} s={s} i={i} />)}
          <p className="mt-6 text-xs text-[var(--steel-dim)]">* {t.intro.capacityNote}</p>
          <div className="mt-14">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">{t.docs.other}</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {DOCS.filter((d) => d.key !== meta.key).map((d) => (
                <Link key={d.key} to={`/documentation/${d.slug}`} data-testid={`doc-nav-${d.key}`} className="group flex items-center justify-between border border-white/12 p-5 hover:border-[var(--gold)] transition-colors">
                  <span className="font-display text-2xl font-bold uppercase">{t.docs[d.key].title}</span>
                  <ArrowUpRight className="h-5 w-5 text-[var(--gold)] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
